import { SourceFamilyTask } from '../api/workItems';
import { parseMailboxStatusDate } from './mailboxStatusDisplay';

/**
 * Where one next step stands, for the source home. Four buckets, all derived
 * from fields the item already has: `state` and `snoozed_until`. Nothing here
 * claims the user did something ("started") that intake may have done for them.
 */
export type FamilyStepStatus = 'new' | 'active' | 'snoozed' | 'done';

/** Order the summary words appear in: handled first, untouched last. */
export const FAMILY_STATUS_ORDER: FamilyStepStatus[] = ['done', 'active', 'snoozed', 'new'];

/** Non-color cue for each bucket so status is not color alone. */
export const FAMILY_STATUS_SHAPE: Record<FamilyStepStatus, 'ring' | 'circle' | 'diamond' | 'square'> = {
    new: 'ring',
    active: 'circle',
    snoozed: 'diamond',
    done: 'square',
};

type StatusFields = Pick<SourceFamilyTask, 'state' | 'snoozed_until'>;

const HAS_TIMEZONE_RE = /(?:[zZ]|[+-]\d{2}:?\d{2})$/;

/**
 * Naive timestamps are UTC (same as mailbox sync). Offset-aware values are
 * instants: do not reject them when the written calendar day differs from UTC.
 */
function parseWhen(value: string | null | undefined): Date | null {
    if (!value) return null;
    const raw = value.trim();
    if (!raw) return null;
    if (HAS_TIMEZONE_RE.test(raw)) {
        const parsed = new Date(raw);
        return Number.isNaN(parsed.getTime()) ? null : parsed;
    }
    return parseMailboxStatusDate(raw);
}

export function familyStepStatus(task: StatusFields, now: Date = new Date()): FamilyStepStatus {
    const state = String(task.state || '').trim().toUpperCase();
    if (state === 'DONE' || state === 'ARCHIVED') return 'done';
    const until = parseWhen(task.snoozed_until);
    if (until && until.getTime() > now.getTime()) return 'snoozed';
    if (state === 'ACTIVE') return 'active';
    return 'new';
}

const DAY_MS = 24 * 60 * 60 * 1000;

function startOfDay(date: Date): number {
    return new Date(date.getFullYear(), date.getMonth(), date.getDate()).getTime();
}

/** "Until 5:00 PM" today, "Until Fri" this week, else "Until Oct 12". */
export function formatSnoozedUntil(until: Date, now: Date = new Date()): string {
    const dayDelta = Math.round((startOfDay(until) - startOfDay(now)) / DAY_MS);
    if (dayDelta <= 0) {
        return `Until ${until.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' })}`;
    }
    if (dayDelta === 1) return 'Until tomorrow';
    if (dayDelta < 7) return `Until ${until.toLocaleDateString(undefined, { weekday: 'short' })}`;
    return `Until ${until.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}`;
}

/**
 * The one word a row carries for its own state. A step nobody has touched
 * says nothing: the plain row is the one to open.
 */
export function familyStepWord(task: StatusFields, now: Date = new Date()): string | null {
    const status = familyStepStatus(task, now);
    if (status === 'done') {
        return String(task.state || '').trim().toUpperCase() === 'ARCHIVED' ? 'Archived' : 'Done';
    }
    if (status === 'snoozed') {
        const until = parseWhen(task.snoozed_until);
        return until ? formatSnoozedUntil(until, now) : 'Snoozed';
    }
    if (status === 'active') return 'Active';
    return null;
}

/** Name a sibling chip so status is not color-only. */
export function familyStepAccessibleName(task: StatusFields & { title?: string | null }, now: Date = new Date()): string {
    const title = String(task.title || '').trim() || 'Untitled Item';
    const word = familyStepWord(task, now);
    return word ? `${title}, ${word}` : `${title}, not started`;
}

/** Soonest remaining snooze end, for a one-shot refresh when it expires. */
export function nextSnoozeExpiryMs(tasks: StatusFields[], now: Date = new Date()): number | null {
    const nowMs = now.getTime();
    let soonest: number | null = null;
    tasks.forEach((task) => {
        if (familyStepStatus(task, now) !== 'snoozed') return;
        const until = parseWhen(task.snoozed_until);
        if (!until) return;
        const at = until.getTime();
        if (at <= nowMs) return;
        if (soonest == null || at < soonest) soonest = at;
    });
    return soonest;
}

function nextLocalMidnightMs(now: Date): number {
    return new Date(now.getFullYear(), now.getMonth(), now.getDate() + 1).getTime();
}

/**
 * Next time the family strip must re-read status words. Snooze expiry, and
 * local midnight so "Until tomorrow" does not stick after the day turns.
 */
export function nextFamilyClockMs(tasks: StatusFields[], now: Date = new Date()): number | null {
    const snooze = nextSnoozeExpiryMs(tasks, now);
    if (snooze == null) return null;
    return Math.min(snooze, nextLocalMidnightMs(now));
}

export interface FamilyStatusCount {
    status: FamilyStepStatus;
    count: number;
    label: string;
}

const COUNT_NOUN: Record<FamilyStepStatus, string> = {
    done: 'done',
    active: 'active',
    snoozed: 'snoozed',
    new: 'not started',
};

/**
 * Words under the family line. Only states that exist get a word. When every
 * step is finished the whole line is one sentence.
 */
export function familyStatusCounts(tasks: StatusFields[], now: Date = new Date()): FamilyStatusCount[] {
    const tally = new Map<FamilyStepStatus, number>();
    tasks.forEach((task) => {
        const status = familyStepStatus(task, now);
        tally.set(status, (tally.get(status) || 0) + 1);
    });
    const total = tasks.length;
    if (total > 0 && tally.get('done') === total) {
        return [{ status: 'done', count: total, label: `All ${total} done` }];
    }
    return FAMILY_STATUS_ORDER
        .filter((status) => (tally.get(status) || 0) > 0)
        .map((status) => {
            const count = tally.get(status) || 0;
            return { status, count, label: `${count} ${COUNT_NOUN[status]}` };
        });
}

/** One sentence for assistive tech, the same facts the segments show. */
export function familyProgressSentence(tasks: StatusFields[], now: Date = new Date()): string {
    const counts = familyStatusCounts(tasks, now);
    if (counts.length === 0) return '';
    return counts.map((entry) => entry.label).join(', ');
}
